import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Printer, ChevronDown } from 'lucide-react';
import { BRAND } from '../brand';

/**
 * Layout compartido para documentos legales (T&C, Política de Privacidad).
 * - Header con logo, tipografía y colores del preset.
 * - Fecha de última actualización.
 * - Índice de contenidos con anchor links.
 * - Secciones colapsables en móvil (expandidas en desktop).
 * - Botón "Descargar/Imprimir PDF" vía window.print (con print stylesheet).
 * Fase 8.b — parametrizado, funciona para ambas marcas.
 */
export const LegalDocLayout = ({ title, updatedAt, sections }) => {
  const navigate = useNavigate();
  const primary = BRAND?.theme?.primary || '#15803D';
  return (
    <div className="min-h-screen bg-slate-50">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: #fff !important; }
          .print-section { page-break-inside: avoid; break-inside: avoid; }
          details { break-inside: avoid; }
          details > summary { list-style: none; }
          details > summary::-webkit-details-marker { display: none; }
          details:not([open]) > *:not(summary) { display: block !important; }
          details { border: none !important; }
        }
      `}</style>

      {/* Header brand */}
      <div className="no-print bg-white border-b sticky top-0 z-30" style={{ borderColor: `${primary}20` }}>
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-slate-900" data-testid="legal-back-btn">
            <ArrowLeft size={16} /> Volver
          </button>
          <img src="/logo.png" alt={BRAND.name} className="h-8" />
          <button onClick={() => window.print()} className="flex items-center gap-1.5 text-white text-sm font-bold px-3 py-1.5 rounded-full transition hover:opacity-90" style={{ background: primary }} data-testid="legal-print-btn">
            <Printer size={14} /> PDF
          </button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6 sm:py-10">
        {/* Título + fecha */}
        <div className="mb-6 print-section">
          <h1 className="text-3xl sm:text-4xl font-black text-slate-900 mb-1" style={{ letterSpacing: '-0.02em' }} data-testid="legal-title">{title}</h1>
          <p className="text-sm text-slate-500"><span className="font-semibold">Última actualización:</span> {updatedAt}</p>
          <p className="text-sm text-slate-500 mt-0.5">Operado por <strong>{BRAND.legalName}</strong>{BRAND.legalCity ? ` · ${BRAND.legalCity}` : ''}</p>
        </div>

        {/* Índice de contenidos */}
        <nav className="no-print bg-white rounded-2xl border p-4 mb-6" data-testid="legal-toc" style={{ borderColor: `${primary}30` }}>
          <p className="text-xs font-black uppercase tracking-widest mb-2" style={{ color: primary }}>Índice</p>
          <ol className="space-y-1 text-sm text-slate-700">
            {sections.map((s, i) => (
              <li key={i}>
                <a href={`#sec-${i}`} className="hover:underline" onClick={(e) => { e.preventDefault(); document.getElementById(`sec-${i}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}>
                  <span className="font-bold" style={{ color: primary }}>{i + 1}.</span> {s.title}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        {/* Secciones */}
        <div className="space-y-3">
          {sections.map((s, i) => (
            <CollapsibleSection key={i} id={`sec-${i}`} num={i + 1} title={s.title}>
              {s.body}
            </CollapsibleSection>
          ))}
        </div>

        <p className="text-xs text-slate-400 text-center mt-10 no-print">© {new Date().getFullYear()} {BRAND.name} · {BRAND.legalName}</p>
      </div>
    </div>
  );
};

const CollapsibleSection = ({ id, num, title, children }) => {
  const [open, setOpen] = useState(true); // desktop default open; mobile CSS could collapse but keep simple
  const primary = BRAND?.theme?.primary || '#15803D';
  return (
    <section id={id} className="print-section bg-white rounded-2xl border border-slate-200 overflow-hidden">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left hover:bg-slate-50 transition no-print" data-testid={`legal-section-toggle-${num}`}>
        <span className="font-bold text-slate-900 text-base sm:text-lg flex items-baseline gap-2">
          <span className="text-sm" style={{ color: primary }}>{num}.</span> {title}
        </span>
        <ChevronDown size={18} className="shrink-0 transition-transform text-slate-400" style={{ transform: open ? 'rotate(180deg)' : 'none' }} />
      </button>
      {/* Título visible sólo en impresión */}
      <h2 className="hidden print:block px-5 pt-4 font-bold text-lg">{num}. {title}</h2>
      {open && (
        <div className="px-5 pb-5 pt-1 text-slate-700 text-sm sm:text-[15px] leading-relaxed space-y-3">
          {children}
        </div>
      )}
    </section>
  );
};

export default LegalDocLayout;
